import { API_BASE_URL } from "../../utilities/constants";
import {
  CreateAddressBook,
  UpdateAddressBook,
  AddressBook,
} from "../../Models/AddressBook";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam";
import { buildAPIUrl } from "../../api/apiHelpers";
import HttpClient from "../../api/httpClient";
import { PaginatedResponse } from "../../Models/Pagination";

/* ===========================================================================
 * SERVICE: AddressBookService (Registry)
 * Strongly typed CRUD wrapper for registry-service AddressBook operations.
 * =========================================================================== */

export class AddressBookService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient();
  }

  /* ---------------------------------------------------------------------------
   * METHOD: all
   * ------------------------------------------------------------------------- */

  /**
   * Retrieves a paginated list of AddressBook entries from the registry-service.
   *
   * Supports pagination, sorting and filtering through `FetchParam`.
   *
   * @param {FetchParam} [params=defaultFetchParam] - Query parameters used to build the request URL.
   * @returns {Promise<PaginatedResponse<AddressBook>>}
   * A promise resolving to a paginated response containing AddressBook entities.
   */
  all(
    params: FetchParam = defaultFetchParam,
  ): Promise<PaginatedResponse<AddressBook>> {
    return this.httpClient.fetch<PaginatedResponse<AddressBook>>(
      buildAPIUrl(`${API_BASE_URL}/registry-service/address-book`, params),
      { method: "GET" },
    );
  }

  /* ---------------------------------------------------------------------------
   * METHOD: get
   * ------------------------------------------------------------------------- */

  /**
   * Retrieves a single AddressBook entry by ID.
   *
   * @param {string} id - The unique identifier of the AddressBook entry.
   * @returns {Promise<AddressBook>} A promise resolving with the requested AddressBook.
   */
  get(id: string): Promise<AddressBook> {
    return this.httpClient.fetch<AddressBook>(
      `${API_BASE_URL}/registry-service/address-book/${id}`,
      { method: "GET" },
    );
  }

  /* ---------------------------------------------------------------------------
   * METHOD: create
   * ------------------------------------------------------------------------- */

  /**
   * Creates a new AddressBook entry in the registry-service.
   *
   * @param {CreateAddressBook} addressBook - Payload representing the new entity.
   * @returns {Promise<AddressBook>} A promise resolving with the created AddressBook.
   */
  create(addressBook: CreateAddressBook): Promise<AddressBook> {
    return this.httpClient.fetch<AddressBook>(
      `${API_BASE_URL}/registry-service/address-book`,
      {
        method: "POST",
        body: JSON.stringify(addressBook),
      },
    );
  }

  /* ---------------------------------------------------------------------------
   * METHOD: edit
   * ------------------------------------------------------------------------- */

  /**
   * Updates an existing AddressBook entry.
   *
   * @param {UpdateAddressBook} addressBook - Updated payload including the ID.
   * @returns {Promise<AddressBook>} A promise resolving with the updated AddressBook.
   */
  edit(addressBook: UpdateAddressBook): Promise<AddressBook> {
    return this.httpClient.fetch<AddressBook>(
      `${API_BASE_URL}/registry-service/address-book/${addressBook.id}`,
      {
        method: "PUT",
        body: JSON.stringify(addressBook),
      },
    );
  }

  /* ---------------------------------------------------------------------------
   * METHOD: delete
   * ------------------------------------------------------------------------- */

  /**
   * Deletes an AddressBook entry by ID.
   *
   * Expected backend response: **204 No Content**
   *
   * @param {string} id - The ID of the entry to delete.
   * @returns {Promise<void>} A promise resolved when deletion succeeds.
   */
  delete(id: string): Promise<void> {
    return this.httpClient.fetch<void>(
      `${API_BASE_URL}/registry-service/address-book/${id}`,
      { method: "DELETE" },
    );
  }
}
